import { useLoader } from '../../store';
import { useClock, useUI } from '@/state/store';
import { fmtUtcShort } from '../format';
import { usePassNow } from '../../passes/usePassNow';
import { usePass } from '../../passes/store';
import { pointingFor } from '@/features/dive/pointing';
import { ChevronRight } from 'lucide-react';
import { groupContactsBySite } from '../contactGroups';
import { OrbitDataStatus } from '../OrbitDataStatus';
import { Link } from 'react-router';
import { RFIMS_SITES } from '@/features/rfims/registry';
import { RfimsVisibilityPanel } from '@/features/rfims/VisibilityPanel';

export function OperationsPanel() {
  const dataset = useLoader((s) => s.dataset);
  const contacts = useLoader((s) => s.activeContacts);
  const selection = useUI((s) => s.selection);
  const live = useClock((s) => s.live);
  const results = useLoader((s) => s.results);
  const now = usePassNow();
  const visibleMissionIds = useLoader((s) => s.visibleMissionIds);
  const visibleMissions = visibleMissionIds ? new Set(visibleMissionIds) : null;
  const missionById = new Map(dataset?.missions.map((m) => [m.id, m]) ?? []);
  const antennaById = new Map(dataset?.antennas.map((a) => [a.id, a]) ?? []);
  const upcoming = Object.entries(results?.contactWindows ?? {})
    .flatMap(([missionId, byAntenna]) =>
      Object.entries(byAntenna).flatMap(([antennaId, windows]) =>
        windows
          .filter(
            (w) =>
              !w.synthetic &&
              (!visibleMissions || visibleMissions.has(missionId)) &&
              w.startMs > now &&
              (!selection.missionId || selection.missionId === missionId) &&
              (!selection.antennaId || selection.antennaId === antennaId),
          )
          .map((w) => ({ ...w, missionId, antennaId })),
      ),
    )
    .sort((a, b) => Math.max(now, a.startMs) - Math.max(now, b.startMs))
    .slice(0, 40);
  const matching = contacts.filter(
    (c) =>
      (!visibleMissions || visibleMissions.has(c.missionId)) &&
      (!selection.antennaId || c.antennaId === selection.antennaId) &&
      (!selection.missionId || c.missionId === selection.missionId),
  );
  const groups = groupContactsBySite(matching, dataset?.antennas ?? []);
  if (dataset?.network === 'rfims') return <section className="flex flex-col gap-3 p-3 text-xs" data-testid="rfims-sites-status">
    <h3 className="font-semibold text-ink">RFIMS monitoring sites</h3>
    <p className="text-ink-2">Open a site for measured telemetry, history and its geographic twin.</p>
    {RFIMS_SITES.map(site => <div key={site.id} className="rounded-control border border-hairline bg-inset/40 p-2">
      <button className="text-left font-medium text-ink hover:text-accent" onClick={() => useUI.getState().select({ antennaId: site.id, missionId: null })}>{site.rmsId} · {site.name}</button>
      <p className="mt-1 font-mono text-[10px] text-ink-3">{site.lat.toFixed(6)}, {site.lon.toFixed(6)}</p>
      <Link className="mt-2 block text-accent underline" to={`/twin/${site.id}`}>View digital twin</Link>
    </div>)}
    <p className="text-ink-3">Current satellite assignments, detection events and calibrated RF coverage are unverified. Host telemetry does not establish a satellite contact.</p>
    <RfimsVisibilityPanel siteId={selection.antennaId} />
  </section>;
  return (
    <section className="flex flex-col gap-2 p-3" data-testid="operations-status">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold text-ink">
          Active contacts <span className="font-mono text-live">{groups.length}</span>
        </h3>
        <span className="text-[10px] text-ink-3">
          {live ? 'Now' : 'Simulation'} · {dataset?.source ?? 'Loading'}
        </span>
      </div>
      <p
        className="text-[10px] text-ink-3"
        title="Visibility calculated from orbital elements and station constraints. This does not confirm an antenna is tracking or receiving data."
      >
        {groups.length} spacecraft/site links · {matching.length} antenna contacts
      </p>
      <div className="flex flex-col gap-0.5" data-testid="active-contact-rows">
        <OrbitDataStatus />
        {dataset?.network === 'noaa' && <details className="mb-1 text-[10px] text-ink-3" data-testid="noaa-scope">
          <summary>NOAA fleet + documented partners · reviewed 15 Oct 2026</summary>
          <p>8 NOAA-owned, 7 partnership-operated and 8 partner spacecraft. The 41 configured antennas and contact targets are a planning inventory, not a verified live operational schedule.</p>
        </details>}
        {dataset?.network === 'noaa_extended' && <p className="mb-1 text-[10px] text-ink-3">International planning catalogue. Inclusion does not imply NOAA ownership or operational service.</p>}
        {dataset?.stationFeedNotice ? <p className="mb-1 text-[10px] text-ink-2" data-testid="station-feed-notice">{dataset.stationFeedNotice}</p> : null}
        {dataset?.catalogSources?.length ? <p className="mb-1 text-[10px] text-ink-3">Orbital data: {dataset.catalogSources.join(', ')}. Positions are predictions from dated elements.</p> : null}
        {groups.slice(0, 100).map((g) => (
          <details
            key={g.key}
            className="group rounded-control border border-hairline bg-inset/40"
            data-testid="active-contact-group"
          >
            <summary
              className="grid h-7 cursor-pointer list-none grid-cols-[0.75rem_minmax(0,1fr)_minmax(0,1.1fr)_auto] items-center gap-1.5 px-2 text-[11px] hover:bg-overlay [&::-webkit-details-marker]:hidden"
              title={`${missionById.get(g.missionId)?.shortName ?? g.missionId} at ${g.site}: ${g.contacts.length} antenna contacts. Expand for individual readings. Predicted visibility; reception unconfirmed.`}
            >
              <ChevronRight aria-hidden className="h-3 w-3 text-ink-3 group-open:rotate-90" />
              <span className="truncate font-medium text-ink">
                {missionById.get(g.missionId)?.shortName ?? g.missionId}
              </span>
              <span className="truncate text-ink-2">{g.site}</span>
              <span className="tabular-nums text-ink-3">{g.contacts.length} ant.</span>
            </summary>
            <div className="flex flex-col gap-0.5 border-t border-hairline px-1 py-1">
              {g.contacts.map((c) => {
                const mission = missionById.get(c.missionId);
                const antenna = antennaById.get(c.antennaId);
                const elevation =
                  mission && antenna ? pointingFor(antenna, mission, now)?.elevationDeg : undefined;
                const label = `${mission?.shortName ?? c.missionId} → ${antenna?.shortName ?? c.antennaId}`;
                const detail = `${label} · ${elevation?.toFixed(1) ?? 'Unknown'}° elevation · Since ${fmtUtcShort(c.sinceMs)}`;
                return (
                  <button
                    type="button"
                    key={`${c.missionId}:${c.antennaId}`}
                    onClick={() =>
                      useUI.getState().select({ missionId: c.missionId, antennaId: c.antennaId })
                    }
                    title={detail}
                    aria-label={detail}
                    className="grid h-7 grid-cols-[minmax(0,1fr)_3.25rem_5.5rem] items-center gap-2 rounded-control px-2 text-left text-[11px] hover:bg-overlay"
                  >
                    <span className="truncate text-ink-2">{antenna?.shortName ?? c.antennaId}</span>
                    <span className="text-right tabular-nums text-ink-2">
                      {elevation?.toFixed(1) ?? '—'}°
                    </span>
                    <span className="text-right text-[10px] tabular-nums text-ink-3">
                      from {new Date(c.sinceMs).toISOString().slice(11, 16)}Z
                    </span>
                  </button>
                );
              })}
            </div>
          </details>
        ))}
      </div>
      {!matching.length && (
        <p className="text-xs text-ink-3">No calculated contacts for this selection at the current time.</p>
      )}
      {groups.length > 100 && (
        <p className="text-xs text-ink-3">
          Showing 100 of {groups.length} links. Select a station or mission to narrow the view.
        </p>
      )}
      <h3 className="mt-2 text-xs font-semibold text-ink">Planned contacts</h3>
      <div className="flex flex-col gap-0.5" data-testid="planned-contact-rows">
        {upcoming.map((p) => {
          const mission = dataset?.missions.find((m) => m.id === p.missionId)?.shortName ?? p.missionId;
          const antenna = dataset?.antennas.find((a) => a.id === p.antennaId)?.shortName ?? p.antennaId;
          const event = p.startMs > now ? 'AOS' : 'LOS';
          const time = p.startMs > now ? p.startMs : p.endMs;
          const detail = `${mission} → ${antenna} · ${event} ${fmtUtcShort(time)} · ${Math.ceil((time - now) / 60_000)} min`;
          return (
            <button
              type="button"
              key={`${p.missionId}:${p.antennaId}:${p.startMs}`}
              title={detail}
              aria-label={detail}
              className="grid h-7 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem] items-center gap-2 rounded-control border border-hairline px-2 text-left text-[11px] hover:bg-overlay"
              onClick={() => {
                usePass.getState().select(p);
              }}
            >
              <span className="truncate text-ink">{mission}</span>
              <span className="truncate text-ink-2">{antenna}</span>
              <span className="text-right tabular-nums text-ink-3">
                {event} {new Date(time).toISOString().slice(11, 16)}Z
              </span>
            </button>
          );
        })}
      </div>
      {!upcoming.length && (
        <p className="text-xs text-ink-3">
          No upcoming window in the current analysis. Open the Pass tab to scan the next 24 hours.
        </p>
      )}
    </section>
  );
}
